import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiStar, FiEdit, FiTrash2, FiFilter, FiSearch, FiPlus,
  FiCalendar, FiTarget, FiTrendingDown, FiTrendingUp, FiImage
} from 'react-icons/fi';
import { 
  getTransformationsAdmin, createTransformation, updateTransformation, deleteTransformation 
} from '../../utils/adminApi';
import ImageUpload from '../../components/admin/ImageUpload';

const AdminTransformations = () => {
  const [transformations, setTransformations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingTransformation, setEditingTransformation] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    before_weight: '',
    after_weight: '',
    duration_months: '',
    before_image: '',
    after_image: '',
    story: '',
    testimonial: '',
    goal_type: 'weight_loss',
    is_featured: false
  });
  
  const [beforeImageUrl, setBeforeImageUrl] = useState('');
  const [afterImageUrl, setAfterImageUrl] = useState('');

  // Image upload handlers
  const handleBeforeImageUpload = (data) => {
    if (data.before_image) {
      setBeforeImageUrl(data.before_image);
      setFormData(prev => ({ ...prev, before_image: data.before_image }));
    }
  };

  const handleAfterImageUpload = (data) => {
    if (data.after_image) {
      setAfterImageUrl(data.after_image);
      setFormData(prev => ({ ...prev, after_image: data.after_image }));
    }
  };

  const handleImageRemove = (type) => {
    if (type === 'before') {
      setBeforeImageUrl('');
      setFormData(prev => ({ ...prev, before_image: '' }));
    } else {
      setAfterImageUrl('');
      setFormData(prev => ({ ...prev, after_image: '' }));
    }
  };

  useEffect(() => {
    fetchTransformations();
  }, [selectedGoal, currentPage]);

  const fetchTransformations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
        ...(selectedGoal && { goal_type: selectedGoal })
      };
      
      const data = await getTransformationsAdmin(params);
      setTransformations(data.transformations);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch transformations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Sending transformation data:', formData);
      
      if (editingTransformation) {
        await updateTransformation(editingTransformation.id, formData);
      } else {
        await createTransformation(formData);
      }
      setShowModal(false);
      setEditingTransformation(null);
      setFormData({
        client_name: '',
        before_weight: '',
        after_weight: '',
        duration_months: '',
        before_image: '',
        after_image: '',
        story: '',
        testimonial: '',
        goal_type: 'weight_loss',
        is_featured: false
      });
      setBeforeImageUrl('');
      setAfterImageUrl('');
      fetchTransformations();
    } catch (error) {
      console.error('Failed to save transformation:', error);
      alert(error.error || 'Failed to save transformation');
    }
  };

  const handleEdit = (transformation) => {
    setEditingTransformation(transformation);
    setFormData({
      client_name: transformation.client_name,
      before_weight: transformation.before_weight,
      after_weight: transformation.after_weight,
      duration_months: transformation.duration_months,
      before_image: transformation.before_image || '',
      after_image: transformation.after_image || '',
      story: transformation.story || '',
      testimonial: transformation.testimonial || '',
      goal_type: transformation.goal_type,
      is_featured: transformation.is_featured
    });
    setBeforeImageUrl(transformation.before_image || '');
    setAfterImageUrl(transformation.after_image || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this transformation?')) {
      try {
        await deleteTransformation(id);
        fetchTransformations();
      } catch (error) {
        console.error('Failed to delete transformation:', error);
      }
    }
  };

  const filteredTransformations = transformations.filter(transformation =>
    transformation.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getGoalColor = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'bg-primary-100 text-primary-800';
      case 'weight_gain': return 'bg-secondary-100 text-secondary-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getGoalIcon = (goal) => {
    return goal === 'weight_loss' ? FiTrendingDown : FiTrendingUp;
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold font-heading text-neutral-900 mb-2">
              Transformation Management
            </h1>
            <p className="text-neutral-600">
              Showcase client success stories and transformations.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Transformation</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>

          {/* Goal Filter */}
          <div className="relative">
            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <select
              value={selectedGoal}
              onChange={(e) => setSelectedGoal(e.target.value)}
              className="pl-10 input-field"
            >
              <option value="">All Goals</option>
              <option value="weight_loss">Weight Loss</option>
              <option value="weight_gain">Weight Gain</option>
            </select>
          </div>

          <div className="flex items-center justify-end">
            <span className="text-sm text-neutral-600">
              Total: {filteredTransformations.length} transformations
            </span>
          </div>
        </div>
      </div>

      {/* Transformations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 animate-pulse">
              <div className="h-4 bg-neutral-200 rounded mb-3"></div>
              <div className="h-3 bg-neutral-200 rounded mb-2"></div>
              <div className="h-3 bg-neutral-200 rounded w-2/3"></div>
            </div>
          ))
        ) : filteredTransformations.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FiStar className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No transformations found</h3>
            <p className="text-neutral-600 mb-4">
              {searchTerm || selectedGoal 
                ? 'Try adjusting your filters' 
                : 'Start by adding your first transformation story'}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary"
            >
              Add First Transformation
            </button>
          </div>
        ) : (
          filteredTransformations.map((transformation) => {
            const GoalIcon = getGoalIcon(transformation.goal_type);
            const weightChange = Math.abs(transformation.after_weight - transformation.before_weight);
            
            return (
              <motion.div
                key={transformation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-primary-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getGoalColor(transformation.goal_type)}`}>
                      <GoalIcon className="w-3 h-3 mr-1" />
                      {transformation.goal_type.replace('_', ' ').toUpperCase()}
                    </span>
                    {transformation.is_featured && (
                      <FiStar className="w-4 h-4 text-accent-500" />
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {transformation.client_name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Weight Change:</span>
                      <span className="font-medium text-primary-600">
                        {weightChange}kg {transformation.goal_type === 'weight_loss' ? 'Lost' : 'Gained'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Duration:</span>
                      <span className="text-neutral-900">{transformation.duration_months} months</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Before → After:</span>
                      <span className="text-neutral-900">
                        {transformation.before_weight}kg → {transformation.after_weight}kg
                      </span>
                    </div>
                  </div>

                  {transformation.story && (
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-3">
                      {transformation.story}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-xs text-neutral-500 mb-4">
                    <span>Added {new Date(transformation.created_at).toLocaleDateString()}</span>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(transformation)}
                      className="flex-1 bg-neutral-100 text-neutral-700 py-2 px-3 rounded-lg hover:bg-neutral-200 transition-colors text-sm font-medium"
                    >
                      <FiEdit className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(transformation.id)}
                      className="bg-danger-100 text-danger-700 py-2 px-3 rounded-lg hover:bg-danger-200 transition-colors text-sm font-medium"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold font-heading mb-4">
                {editingTransformation ? 'Edit Transformation' : 'Add New Transformation'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="input-field"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Before Weight (kg) *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.1"
                      value={formData.before_weight}
                      onChange={(e) => setFormData({...formData, before_weight: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      After Weight (kg) *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.1"
                      value={formData.after_weight}
                      onChange={(e) => setFormData({...formData, after_weight: e.target.value})}
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Duration (months) *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.duration_months}
                      onChange={(e) => setFormData({...formData, duration_months: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Goal Type *
                    </label>
                    <select
                      required
                      value={formData.goal_type}
                      onChange={(e) => setFormData({...formData, goal_type: e.target.value})}
                      className="input-field"
                    >
                      <option value="weight_loss">Weight Loss</option>
                      <option value="weight_gain">Weight Gain</option>
                    </select>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      <FiImage className="inline w-4 h-4 mr-1" />
                      Before Image
                    </label>
                    <ImageUpload
                      endpoint="/admin/upload/transformation-images"
                      onUploadSuccess={handleBeforeImageUpload}
                      onUploadError={(error) => console.error('Before image upload error:', error)}
                      currentImage={beforeImageUrl}
                      onImageRemove={() => handleImageRemove('before')}
                      label="Upload Before Image"
                      category="transformations"
                      prefix="before"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      <FiImage className="inline w-4 h-4 mr-1" />
                      After Image
                    </label>
                    <ImageUpload
                      endpoint="/admin/upload/transformation-images"
                      onUploadSuccess={handleAfterImageUpload}
                      onUploadError={(error) => console.error('After image upload error:', error)}
                      currentImage={afterImageUrl}
                      onImageRemove={() => handleImageRemove('after')}
                      label="Upload After Image"
                      category="transformations"
                      prefix="after"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Success Story
                  </label>
                  <textarea
                    rows={3}
                    value={formData.story}
                    onChange={(e) => setFormData({...formData, story: e.target.value})}
                    className="input-field"
                    placeholder="Describe the transformation journey..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Client Testimonial
                  </label>
                  <textarea
                    rows={3}
                    value={formData.testimonial}
                    onChange={(e) => setFormData({...formData, testimonial: e.target.value})}
                    className="input-field"
                    placeholder="Client's own words about their experience..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_featured" className="ml-2 text-sm text-neutral-700">
                    Feature on homepage
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                  >
                    {editingTransformation ? 'Update' : 'Add'} Transformation
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTransformations;